import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import PillBadge from './PillBadge';

gsap.registerPlugin(ScrollTrigger);

// Animated Image Carousel Component with Randomized Effects
const ImageCarousel = ({ images, interval = 3000, startDelay = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animationType, setAnimationType] = useState(0);

  // Array of different animation variants
  const animationVariants = [
    // Fade with slight zoom in
    {
      initial: { opacity: 0, scale: 1.1 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 1 }
    },
    // Fade with slight zoom out
    {
      initial: { opacity: 0, scale: 0.95 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 0.95 }
    },
    // Slide from right
    {
      initial: { opacity: 0, x: 100 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -100 }
    },
    // Slide from left
    {
      initial: { opacity: 0, x: -100 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: 100 }
    },
    // Slide from top
    {
      initial: { opacity: 0, y: -50 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: 50 }
    },
    // Slide from bottom
    {
      initial: { opacity: 0, y: 50 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -50 }
    },
    // Rotate fade
    {
      initial: { opacity: 0, scale: 1.2, rotate: 5 },
      animate: { opacity: 1, scale: 1, rotate: 0 },
      exit: { opacity: 0, scale: 0.9, rotate: -5 }
    },
    // Cross dissolve
    {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 }
    }
  ];

  useEffect(() => {
    let timer;
    // Start with random delay to desynchronize carousels
    const startTimer = setTimeout(() => {
      timer = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
        // Randomize animation type on each transition
        setAnimationType(Math.floor(Math.random() * animationVariants.length));
      }, interval);
    }, startDelay);

    return () => {
      clearTimeout(startTimer);
      if (timer) clearInterval(timer);
    };
  }, [images.length, interval, startDelay, animationVariants.length]);

  const currentAnimation = animationVariants[animationType];

  return (
    <div className="relative w-full h-full overflow-hidden">
      <AnimatePresence initial={false}>
        <motion.img
          key={currentIndex}
          src={images[currentIndex]}
          alt={`Slide ${currentIndex + 1}`}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
          initial={currentAnimation.initial}
          animate={currentAnimation.animate}
          exit={currentAnimation.exit}
          transition={{ duration: 1.2, ease: "easeInOut" }}
        />
      </AnimatePresence>
    </div>
  );
};

const PortfolioSection = () => {
  const navigate = useNavigate();
  const portfolioRef = useRef(null);
  const [projects, setProjects] = useState([
    {
      id: 1,
      title: "Loading...",
      year: "2024",
      images: [
        "/Digital and TV commercials/vlcsnap-2025-10-08-13h41m25s762.png",
        "/Digital and TV commercials/vlcsnap-2025-10-08-13h42m14s227.png",
        "/Digital and TV commercials/vlcsnap-2025-10-08-13h42m53s753.png",
        "/Digital and TV commercials/vlcsnap-2025-10-08-13h43m35s167.png",
        "/Digital and TV commercials/vlcsnap-2025-10-08-13h44m47s742.png",
        "/Digital and TV commercials/vlcsnap-2025-10-08-13h45m09s329.png",
        "/Digital and TV commercials/vlcsnap-2025-10-08-13h45m43s181.png",
        "/Digital and TV commercials/vlcsnap-2025-10-08-13h45m58s387.png"
      ],
      playlistId: "PLv7ad17r_DuB8jqyOW7Q8V4TF65TtWBtl"
    },
    {
      id: 2,
      title: "Loading...",
      year: "2024",
      images: [
        "/Reel/vlcsnap-2025-10-08-13h36m47s888.png",
        "/Reel/vlcsnap-2025-10-08-13h36m59s048.png",
        "/Reel/vlcsnap-2025-10-08-13h37m43s531.png",
        "/Reel/vlcsnap-2025-10-08-13h38m10s225.png",
        "/Reel/vlcsnap-2025-10-08-13h38m31s180.png",
        "/Reel/vlcsnap-2025-10-08-13h39m17s693.png",
        "/Reel/vlcsnap-2025-10-08-13h39m45s728.png",
        "/Reel/vlcsnap-2025-10-08-13h40m08s692.png"
      ],
      playlistId: "PLv7ad17r_DuAXlcrSWzh39pGNpdpkB8p8"
    },
    {
      id: 3,
      title: "Loading...",
      year: "2023",
      images: [
        "/Live concert and show/vlcsnap-2025-10-08-13h30m03s058.png",
        "/Live concert and show/vlcsnap-2025-10-08-13h30m25s190.png",
        "/Live concert and show/vlcsnap-2025-10-08-13h30m49s722.png",
        "/Live concert and show/vlcsnap-2025-10-08-13h33m11s896.png",
        "/Live concert and show/vlcsnap-2025-10-08-13h34m34s502.png",
        "/Live concert and show/vlcsnap-2025-10-08-13h35m00s690.png"
      ],
      playlistId: "PLv7ad17r_DuCgHWG3xRL2sbHZdODmivf0"
    },
    {
      id: 4,
      title: "Loading...",
      year: "2024",
      images: [
        "/Corporate Films/vlcsnap-2025-10-08-13h49m01s945.png",
        "/Corporate Films/vlcsnap-2025-10-08-13h49m06s757.png",
        "/Corporate Films/vlcsnap-2025-10-08-13h49m49s539.png",
        "/Corporate Films/vlcsnap-2025-10-08-13h49m58s697.png",
        "/Corporate Films/vlcsnap-2025-10-08-13h50m36s405.png",
        "/Corporate Films/vlcsnap-2025-10-08-13h50m58s001.png",
        "/Corporate Films/vlcsnap-2025-10-08-13h51m06s544.png",
        "/Corporate Films/vlcsnap-2025-10-08-13h51m29s168.png",
        "/Corporate Films/vlcsnap-2025-10-08-13h51m48s910.png"
      ],
      playlistId: "PLv7ad17r_DuBINNL70CMur08-k2JX-upc"
    }
  ]);

  const YOUTUBE_API_KEY = 'AIzaSyDrpIx_Ouc1XFb4bEZO6Bs32GLg9YZxzoM';

  useEffect(() => {
    const fetchPlaylistTitles = async () => {
      const updatedProjects = await Promise.all(
        projects.map(async (project) => {
          try {
            const response = await fetch(
              `https://www.googleapis.com/youtube/v3/playlists?part=snippet&id=${project.playlistId}&key=${YOUTUBE_API_KEY}`
            );
            
            if (response.ok) {
              const data = await response.json();
              if (data.items && data.items.length > 0) {
                const playlist = data.items[0];
                return {
                  ...project,
                  title: playlist.snippet.title,
                };
              }
            }
          } catch (err) {
            console.error(`Error fetching playlist ${project.playlistId}:`, err);
          }
          return project;
        })
      );
      
      setProjects(updatedProjects);
    };

    fetchPlaylistTitles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Add 3D scroll effect for PortfolioSection
  useEffect(() => {
    if (!portfolioRef.current) return;

    const scrollTrigger = ScrollTrigger.create({
      trigger: "#portfolio",
      start: "top bottom",
      end: "bottom top",
      scrub: 1,
      onUpdate: (self) => {
        const progress = self.progress;
        // Move PortfolioSection forward in 3D space as it comes into view
        const forwardMovement = progress * 300;
        gsap.set(portfolioRef.current, {
          z: forwardMovement,
          force3D: true
        });
      }
    });

    return () => {
      scrollTrigger.kill();
    };
  }, []);

  return (
    <section 
      ref={portfolioRef}
      id="portfolio" 
      className="pt-20 pb-20 bg-black"
      style={{
        position: 'relative',
        zIndex: 100,
        transform: 'translateZ(0)', // Force hardware acceleration
        transformStyle: 'preserve-3d'
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-6"
          >
            <PillBadge text={"Portfolio"} icon={''} className="mb-8" />
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl tracking-tight"
          >
            <span className="text-[#999999]">My Latest </span>
            <span className="text-white">Projects</span>
          </motion.h2>
        </motion.div>

        {/* Simple 2x2 Grid Layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group cursor-pointer"
              onClick={() => {
                navigate(`/playlist/${project.playlistId}`);
              }}
            >
              <div className="relative bg-[#0E0E0E] border border-[#333333] rounded-2xl overflow-hidden hover:bg-[#0f0f0f] transition-all duration-300 hover:border-[#444444] hover:shadow-xl hover:shadow-black/20 h-full hover:scale-105">
                <div className="aspect-[16/9] bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] relative overflow-hidden">
                  <ImageCarousel
                    images={project.images}
                    interval={7000}
                    startDelay={index * 1200}
                  />
                  <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/80 to-transparent z-10 pointer-events-none"></div>
                </div>
                <div className="p-4">
                  <h3 className="text-white text-base md:text-lg font-bold leading-tight mb-2 truncate" title={project.title}>
                    {project.title}
                  </h3>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
